import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../client";
import type {
  Site,
  SiteCreate,
  SiteListRow,
  SiteUpdate,
} from "../types";

const SITES_KEY = ["sites"] as const;

export function useSites() {
  return useQuery<SiteListRow[]>({
    queryKey: SITES_KEY,
    queryFn: () => apiRequest("/api/sites"),
  });
}

export function useSite(id: string | undefined) {
  return useQuery<Site>({
    queryKey: [...SITES_KEY, id],
    queryFn: () => apiRequest(`/api/sites/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation<Site, Error, SiteCreate>({
    mutationFn: (payload) =>
      apiRequest("/api/sites", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SITES_KEY });
    },
  });
}

export function useUpdateSite(id: string) {
  const qc = useQueryClient();
  return useMutation<Site, Error, SiteUpdate>({
    mutationFn: (payload) =>
      apiRequest(`/api/sites/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: (site) => {
      qc.setQueryData([...SITES_KEY, id], site);
      qc.invalidateQueries({ queryKey: SITES_KEY });
    },
  });
}

export function useDeleteSite() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiRequest(`/api/sites/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SITES_KEY });
    },
  });
}
